// Spec 45 component-behavior — cmp_ds_tooltip + aria_tooltip.
//
// Tooltip is the SIMPLEST overlay — non-interactive, hover/focus-
// driven, role='tooltip' + aria-describedby on the trigger.
//
// Critical a11y rules:
//   * Tooltip content NEVER carries interactive elements. Tooltips
//     can't be focused; if the content needs interaction, use Popover.
//   * Trigger must remain focusable + the tooltip must appear on
//     KEYBOARD focus, not just mouse hover (WCAG 1.4.13).
//   * Escape dismisses while focus is on the trigger.
//   * Hover open delay reduces flicker; close has no delay so dismiss
//     feels immediate.
//
// Composition pattern (consumer):
//   const [open, setOpen] = useState(false);
//   <button
//     onMouseEnter={() => setOpen(true)}
//     onMouseLeave={() => setOpen(false)}
//     onFocus={() => setOpen(true)}
//     onBlur={() => setOpen(false)}
//     onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
//     aria-describedby={open ? "btn-tip" : undefined}
//   >
//     Save
//   </button>
//   <Tooltip id="btn-tip" open={open}>Save the form (Mod+S)</Tooltip>
//
// We don't auto-wire this composition — Phase 4b.5 ships the surface;
// a `useTooltip` hook composing trigger + tooltip ships in 4b.X
// alongside the floating-ui upgrade.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface TooltipProps extends Omit<HTMLAttributes<HTMLDivElement>, "role" | "id"> {
  readonly id: string;
  readonly open: boolean;
  readonly children?: ReactNode;
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  ({ id, open, className, children, ...rest }, ref) => {
    if (!open) return null;
    return (
      <div
        ref={ref}
        id={id}
        role="tooltip"
        data-component="tooltip"
        className={cx("nai-tooltip", className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
Tooltip.displayName = "Tooltip";
